import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';

export function TaskFilterTabs({
    tabs,
    activeTab,
    onTabChange,
    ariaLabel = 'Filtrar',
}) {
    const trackRef = useRef(null);
    const scrollRef = useRef(null);
    const tabRefs = useRef({});
    const [indicator, setIndicator] = useState({ left: 0, width: 0 });

    const updateIndicator = useCallback(() => {
        const track = trackRef.current;
        const activeEl = tabRefs.current[activeTab];
        if (!track || !activeEl) return;

        const trackRect = track.getBoundingClientRect();
        const tabRect = activeEl.getBoundingClientRect();

        setIndicator({
            left: tabRect.left - trackRect.left,
            width: tabRect.width,
        });
    }, [activeTab]);

    const scrollActiveTabIntoView = useCallback(() => {
        const scrollEl = scrollRef.current;
        const activeEl = tabRefs.current[activeTab];
        if (!scrollEl || !activeEl) return;

        const scrollRect = scrollEl.getBoundingClientRect();
        const tabRect = activeEl.getBoundingClientRect();
        const padding = 8;

        if (tabRect.left < scrollRect.left + padding) {
            scrollEl.scrollBy({
                left: tabRect.left - scrollRect.left - padding,
                behavior: 'smooth',
            });
        } else if (tabRect.right > scrollRect.right - padding) {
            scrollEl.scrollBy({
                left: tabRect.right - scrollRect.right + padding,
                behavior: 'smooth',
            });
        }
    }, [activeTab]);

    useLayoutEffect(() => {
        updateIndicator();
        scrollActiveTabIntoView();
    }, [updateIndicator, scrollActiveTabIntoView, tabs, activeTab]);

    useEffect(() => {
        const track = trackRef.current;
        const scrollEl = scrollRef.current;
        if (!track) return undefined;

        const observer = new ResizeObserver(() => updateIndicator());
        observer.observe(track);
        window.addEventListener('resize', updateIndicator);
        scrollEl?.addEventListener('scroll', updateIndicator, { passive: true });

        return () => {
            observer.disconnect();
            window.removeEventListener('resize', updateIndicator);
            scrollEl?.removeEventListener('scroll', updateIndicator);
        };
    }, [updateIndicator]);

    return (
        <div className="task-filter-tabs" role="tablist" aria-label={ariaLabel}>
            <div className="task-filter-tabs__scroll" ref={scrollRef}>
                <div className="task-filter-tabs__track" ref={trackRef}>
                    <span
                        className="task-filter-tabs__indicator"
                        style={{
                            transform: `translateX(${indicator.left}px)`,
                            width: indicator.width,
                        }}
                        aria-hidden="true"
                    />
                    {tabs.map((tab) => {
                        const isActive = activeTab === tab.id;

                        return (
                            <button
                                key={tab.id}
                                type="button"
                                role="tab"
                                aria-selected={isActive}
                                ref={(el) => {
                                    tabRefs.current[tab.id] = el;
                                }}
                                className={`task-filter-tabs__tab${isActive ? ' is-active' : ''}`}
                                onClick={() => onTabChange(tab.id)}
                            >
                                <span className="task-filter-tabs__label">{tab.label}</span>
                            </button>
                        );
                    })}
                </div>
            </div>
        </div>
    );
}
